/**
 * Oath Power Log Service
 * API calls for power log operations using Oath authentication
 * Routes to /oath/powerlogs/* endpoints in OathPowerLogController
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';
import { formatCSVCurrencyColumns } from '../components/app/powerreporting/utils/csvUtils';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - handles BaseResponse wrapper
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// POWER LOG READ OPERATIONS
// ============================================

/**
 * Get paginated power log entries with optional filtering
 * @param {Object} params - Filter parameters
 * @param {number} params.agencyId - Required agency ID
 * @param {number} params.pageNumber - Page number (1-indexed, default: 1)
 * @param {number} params.pageSize - Items per page (default: 25, max: 100)
 * @param {Date|string} params.fromDate - Optional start date filter
 * @param {Date|string} params.toDate - Optional end date filter
 * @param {string} params.searchPowerNo - Optional power number search
 * @param {string} params.searchDefendantName - Optional defendant name search
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getPowerLogs = async ({
  agencyId,
  pageNumber = 1,
  pageSize = 25,
  fromDate,
  toDate,
  searchPowerNo,
  searchDefendantName,
  searchCaseNumber,
  searchDisposition,
  searchAgentName,
  searchCollateralReceiptNo,
  searchCollateral,
  dispositionFromDate,
  dispositionToDate,
  minAmountOfBond,
  maxAmountOfBond,
  minPremiumCharged,
  maxPremiumCharged,
  minCompanyPremium,
  maxCompanyPremium,
  signal
}) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (fromDate) {
      const dateStr = fromDate instanceof Date ? fromDate.toISOString().split('T')[0] : fromDate;
      params.append('fromDate', dateStr);
    }
    if (toDate) {
      const dateStr = toDate instanceof Date ? toDate.toISOString().split('T')[0] : toDate;
      params.append('toDate', dateStr);
    }
    if (searchPowerNo) params.append('searchPowerNo', searchPowerNo);
    if (searchDefendantName) params.append('searchDefendantName', searchDefendantName);
    if (searchCaseNumber) params.append('searchCaseNumber', searchCaseNumber);
    if (searchDisposition) params.append('searchDisposition', searchDisposition);
    if (searchAgentName) params.append('searchAgentName', searchAgentName);
    if (searchCollateralReceiptNo) params.append('searchCollateralReceiptNo', searchCollateralReceiptNo);
    if (searchCollateral) params.append('searchCollateral', searchCollateral);
    if (dispositionFromDate) {
      const dateStr = dispositionFromDate instanceof Date ? dispositionFromDate.toISOString().split('T')[0] : dispositionFromDate;
      params.append('dispositionFromDate', dateStr);
    }
    if (dispositionToDate) {
      const dateStr = dispositionToDate instanceof Date ? dispositionToDate.toISOString().split('T')[0] : dispositionToDate;
      params.append('dispositionToDate', dateStr);
    }
    if (minAmountOfBond) params.append('minAmountOfBond', minAmountOfBond);
    if (maxAmountOfBond) params.append('maxAmountOfBond', maxAmountOfBond);
    if (minPremiumCharged) params.append('minPremiumCharged', minPremiumCharged);
    if (maxPremiumCharged) params.append('maxPremiumCharged', maxPremiumCharged);
    if (minCompanyPremium) params.append('minCompanyPremium', minCompanyPremium);
    if (maxCompanyPremium) params.append('maxCompanyPremium', maxCompanyPremium);

    const response = await authFetch(`${BASE_URL}oath/powerlogs?${params}`, { signal });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get power logs', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, error: 'cancelled', data: null };
    }
    console.error('Get power logs error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get a single power log entry by ID
 * @param {number} logId - The log entry ID
 * @param {number} agencyId - Required agency ID for authorization
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getPowerLogById = async (logId, agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerlogs/${logId}?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get power log entry' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get power log by ID error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// POWER LOG WRITE OPERATIONS
// ============================================

/**
 * Create a new power log entry
 * @param {Object} logData - The log entry data
 * @param {number} logData.agencyId - Required agency ID
 * @param {string} logData.powerNo - Required power number
 * @param {string} logData.name - Optional defendant name
 * @param {Date|string} logData.dateExecuted - Optional execution date
 * @param {string} logData.caseNumber - Optional case number
 * @param {number} logData.amountOfBond - Optional bond amount
 * @param {number} logData.premiumCharged - Optional premium charged
 * @param {number} logData.companyPremium - Optional company premium
 * @param {string} logData.disposition - Optional disposition
 * @param {Date|string} logData.dispositionDate - Optional disposition date
 * @param {string} logData.collateralReceiptNo - Optional collateral receipt number
 * @param {string} logData.collateral - Optional collateral description
 * @param {string} logData.agentsName - Optional agent name
 * @returns {Promise<{success: boolean, logId?: number, error?: string}>}
 */
export const createPowerLog = async (logData) => {
  try {
    // Format dates for API
    const payload = {
      ...logData,
      dateExecuted: logData.dateExecuted
        ? (logData.dateExecuted instanceof Date
            ? logData.dateExecuted.toISOString()
            : logData.dateExecuted)
        : null,
      dispositionDate: logData.dispositionDate
        ? (logData.dispositionDate instanceof Date
            ? logData.dispositionDate.toISOString()
            : logData.dispositionDate)
        : null,
    };

    const response = await authFetch(`${BASE_URL}oath/powerlogs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to create power log entry' };
    }

    return { success: true, logId: result.logId };
  } catch (error) {
    console.error('Create power log error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Update an existing power log entry
 * @param {number} logId - The log entry ID to update
 * @param {Object} logData - The updated log entry data (same structure as create)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updatePowerLog = async (logId, logData) => {
  try {
    // Format dates for API
    const payload = {
      ...logData,
      dateExecuted: logData.dateExecuted
        ? (logData.dateExecuted instanceof Date
            ? logData.dateExecuted.toISOString()
            : logData.dateExecuted)
        : null,
      dispositionDate: logData.dispositionDate
        ? (logData.dispositionDate instanceof Date
            ? logData.dispositionDate.toISOString()
            : logData.dispositionDate)
        : null,
    };

    const response = await authFetch(`${BASE_URL}oath/powerlogs/${logId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to update power log entry' };
    }

    return { success: true };
  } catch (error) {
    console.error('Update power log error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Delete a power log entry
 * @param {number} logId - The log entry ID to delete
 * @param {number} agencyId - Required agency ID for authorization
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deletePowerLog = async (logId, agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerlogs/${logId}?agencyId=${agencyId}`, {
      method: 'DELETE',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to delete power log entry' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete power log error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// EXPORT OPERATIONS
// ============================================

/**
 * Export power log entries to CSV
 * @param {Object} params - Filter parameters (same as getPowerLogs)
 * @param {boolean} downloadFile - If true, triggers file download; if false, returns CSV content
 * @returns {Promise<{success: boolean, csvContent?: string, fileName?: string, error?: string}>}
 */
export const exportPowerLogs = async ({
  agencyId,
  fromDate,
  toDate,
  searchPowerNo,
  searchDefendantName,
  searchCaseNumber,
  searchDisposition,
  searchAgentName,
  searchCollateralReceiptNo,
  searchCollateral,
  dispositionFromDate,
  dispositionToDate,
  minAmountOfBond,
  maxAmountOfBond,
  minPremiumCharged,
  maxPremiumCharged,
  minCompanyPremium,
  maxCompanyPremium
}, downloadFile = true) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      asFile: downloadFile.toString(),
    });

    if (fromDate) {
      const dateStr = fromDate instanceof Date ? fromDate.toISOString().split('T')[0] : fromDate;
      params.append('fromDate', dateStr);
    }
    if (toDate) {
      const dateStr = toDate instanceof Date ? toDate.toISOString().split('T')[0] : toDate;
      params.append('toDate', dateStr);
    }
    if (searchPowerNo) params.append('searchPowerNo', searchPowerNo);
    if (searchDefendantName) params.append('searchDefendantName', searchDefendantName);
    if (searchCaseNumber) params.append('searchCaseNumber', searchCaseNumber);
    if (searchDisposition) params.append('searchDisposition', searchDisposition);
    if (searchAgentName) params.append('searchAgentName', searchAgentName);
    if (searchCollateralReceiptNo) params.append('searchCollateralReceiptNo', searchCollateralReceiptNo);
    if (searchCollateral) params.append('searchCollateral', searchCollateral);
    if (dispositionFromDate) {
      const dateStr = dispositionFromDate instanceof Date ? dispositionFromDate.toISOString().split('T')[0] : dispositionFromDate;
      params.append('dispositionFromDate', dateStr);
    }
    if (dispositionToDate) {
      const dateStr = dispositionToDate instanceof Date ? dispositionToDate.toISOString().split('T')[0] : dispositionToDate;
      params.append('dispositionToDate', dateStr);
    }
    if (minAmountOfBond) params.append('minAmountOfBond', minAmountOfBond);
    if (maxAmountOfBond) params.append('maxAmountOfBond', maxAmountOfBond);
    if (minPremiumCharged) params.append('minPremiumCharged', minPremiumCharged);
    if (maxPremiumCharged) params.append('maxPremiumCharged', maxPremiumCharged);
    if (minCompanyPremium) params.append('minCompanyPremium', minCompanyPremium);
    if (maxCompanyPremium) params.append('maxCompanyPremium', maxCompanyPremium);

    const response = await authFetch(`${BASE_URL}oath/powerlogs/export?${params}`);

    if (!response.ok) {
      const result = await parseResponse(response);
      return { success: false, error: result.message || 'Failed to export power logs' };
    }

    if (downloadFile) {
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `PowerLog_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, '');
        }
      }

      // Check for warning message in header (for file downloads)
      const warningMessage = response.headers.get('X-Export-Warning');

      // Create blob, format currency columns, and trigger download
      const blob = await response.blob();
      const csvText = await blob.text();
      const formattedText = formatCSVCurrencyColumns(csvText);
      const formattedBlob = new Blob([formattedText], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(formattedBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, fileName, message: warningMessage || null };
    } else {
      // Return CSV content as JSON
      const result = await parseResponse(response);
      return {
        success: true,
        csvContent: result.csvContent,
        fileName: result.fileName,
        message: result.message || null
      };
    }
  } catch (error) {
    console.error('Export power logs error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// ALL POWERS (for Power Log Dropdown)
// ============================================

/**
 * Search powers for an agency regardless of status (for power log dropdown)
 * @param {number} agencyId - Required agency ID
 * @param {string} searchTerm - Power number search term (min 2 chars)
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getAllPowersForLog = async (agencyId, searchTerm = '') => {
  try {
    const params = new URLSearchParams({ agencyId: agencyId.toString() });
    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }

    const response = await authFetch(`${BASE_URL}oath/powerlogs/all-powers?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get powers', data: [] };
    }

    const items = Array.isArray(result) ? result : [];
    return { success: true, data: items };
  } catch (error) {
    console.error('Get all powers for log error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

// ============================================
// VALIDATION OPERATIONS
// ============================================

/**
 * Validate a power number before creating a log entry
 * @param {number} agencyId - Required agency ID
 * @param {string} powerNo - The power number to validate
 * @returns {Promise<{success: boolean, isValidPower?: boolean, alreadyInLog?: boolean, message?: string, error?: string}>}
 */
export const validatePowerNumber = async (agencyId, powerNo) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      powerNo: powerNo.trim(),
    });

    const response = await authFetch(`${BASE_URL}oath/powerlogs/validate-power?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to validate power number' };
    }

    return {
      success: true,
      isValidPower: result.isValidPower,
      alreadyInLog: result.alreadyInLog,
      powerStatusCd: result.powerStatusCd || null,
      message: result.message
    };
  } catch (error) {
    console.error('Validate power number error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get prefill data for a new log entry from an executed (E) power.
 * Returns stored defendant name, execution date, executing agent, case number,
 * bond amount, and premium so the user doesn't have to re-type them.
 * @param {number} agencyId - Required agency ID
 * @param {string} powerNo - The executed power number
 * @returns {Promise<{success: boolean, data?: Object|null, error?: string}>} data is the
 *   prefill object, or null when the power isn't an executed power for the agency
 */
export const getExecutedPowerPrefill = async (agencyId, powerNo) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      powerNo: powerNo.trim(),
    });

    const response = await authFetch(`${BASE_URL}oath/powerlogs/power-prefill?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get power prefill', data: null };
    }

    return { success: true, data: result || null };
  } catch (error) {
    console.error('Get executed power prefill error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// CONSTANTS
// ============================================

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;
export const AUTO_REFRESH_INTERVAL_MS = 30000;
export const DEBOUNCE_DELAY_MS = 400;

// Field length constraints (matching backend)
export const FIELD_LIMITS = {
  POWER_NO: 16,
  CASE_NUMBER: 50,
  COLLATERAL_RECEIPT_NO: 100,
  AGENTS_NAME: 200,
  DEFENDANT_NAME: 300,
};
